import React, { useState, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Stack, Text, Flex, Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Card } from '@atlaskit/smart-card';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import VisuallyHidden from '@atlaskit/visually-hidden';

import {
	RefetchAudioButton,
	getActiveContent,
	getInterimSupportingContent,
	setInterimSupportingContent,
	isSourceContentDiverged,
} from '@atlassian/ai-audio';

import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { getSmartCardRenderers } from '@confluence/fabric-extension-lib/entry-points/getSmartCardRenderers.ts';

import { SourcesInput } from './sub-components/SourcesInput.tsx';

type AdditionalResource = {
	link: string;
	ari: string;
};

const styles = cssMap({
	sourcesContentContainer: {
		paddingInline: token('space.100'),
		textAlign: 'left',
	},
	cardContainer: {
		overflow: 'hidden',
		minHeight: '27px', // same height as the Card component, to prevent layout shift
	},
	smartLinkContainer: {
		maxHeight: '24px',
		overflow: 'hidden',
	},
	buttonContainer: {
		paddingInline: token('space.050'),
	},
});

const i18n = defineMessages({
	sourcesDescription: {
		id: 'audio.audio-popup.sources-description',
		defaultMessage: 'Customize briefings by adding sources. Hear how all the info works together.',
		description: 'The description for the sources panel in the audio popup',
	},
	thisPageHeader: {
		id: 'audio.audio-popup.sources-this-page-header',
		defaultMessage: 'This page',
		description: 'The header for the this page section in the sources panel',
	},
	additionalSourcesHeader: {
		id: 'audio.audio-popup.sources-additional-sources-header',
		defaultMessage: 'Additional sources',
		description: 'The header for the additional sources section in the sources panel',
	},
	refetchAudioButtonText: {
		id: 'audio.audio-popup.sources-tab.refetch-audio-button',
		defaultMessage: 'Re-start briefing',
		description: 'Text for a button that refetches audio and plays it when clicked',
	},
	sourceAddedAnnouncement: {
		id: 'audio.audio-popup.sources-tab.source-added-announcement',
		defaultMessage: 'Source added successfully',
		description: 'Screen reader announcement when a source is successfully added',
	},
	sourceRemovedAnnouncement: {
		id: 'audio.audio-popup.sources-tab.source-removed-announcement',
		defaultMessage: 'Source removed successfully',
		description: 'Screen reader announcement when a source is successfully removed',
	},
});

export const SourcesPanel = () => {
	const { formatMessage } = useIntl();

	const smartCardProviderProps = getSmartCardRenderers();

	const activeContent = getActiveContent();
	const activeContentUrl = activeContent.mainContentLink;
	const latestAdditionalSources = getInterimSupportingContent() ?? activeContent.supportingContent;

	const [localAdditionalResources, setLocalAdditionalResources] = useState<AdditionalResource[]>(
		() =>
			latestAdditionalSources.map((resource) => ({
				link: resource.link,
				ari: resource.ari,
			})),
	);
	// State for ARIA live region announcements
	const [announcement, setAnnouncement] = useState<string>('');

	// Sync local changes to interim supporting content for onBack functionality
	useEffect(() => {
		setInterimSupportingContent(localAdditionalResources);
	}, [localAdditionalResources]);

	const addAdditionalResource = useCallback(
		(newResource: AdditionalResource) => {
			setLocalAdditionalResources((prev) => [...prev, newResource]);
			setAnnouncement(formatMessage(i18n.sourceAddedAnnouncement));
		},
		[formatMessage],
	);

	const removeAdditionalResource = useCallback(
		(ariToRemove: string) => {
			setLocalAdditionalResources((prev) =>
				prev.filter((resource) => resource.ari !== ariToRemove),
			);
			setAnnouncement(formatMessage(i18n.sourceRemovedAnnouncement));
		},
		[formatMessage],
	);

	const sourcesHaveDiverged = isSourceContentDiverged(
		activeContent.supportingContent,
		latestAdditionalSources,
	);

	return (
		<ConfluenceSmartCardProvider {...smartCardProviderProps}>
			{/* ARIA live region for screen reader announcements */}
			<VisuallyHidden>
				<div role="status" aria-live="polite" aria-atomic="true">
					{announcement}
				</div>
			</VisuallyHidden>
			<Stack space="space.050">
				<Stack xcss={styles.sourcesContentContainer} space="space.200">
					<Text size="small" color="color.text.subtlest">
						{formatMessage(i18n.sourcesDescription)}
					</Text>
					<Stack space="space.200">
						<Heading size="xxsmall">{formatMessage(i18n.thisPageHeader)}</Heading>
						<Flex xcss={styles.cardContainer} alignItems="center">
							<Card url={activeContentUrl} appearance="inline" truncateInline />
						</Flex>
					</Stack>
					<Stack space="space.200">
						<Heading size="xxsmall">{formatMessage(i18n.additionalSourcesHeader)}</Heading>
						<Stack space="space.075">
							{localAdditionalResources.map((resource) => (
								<Inline key={resource.ari} spread="space-between" xcss={styles.smartLinkContainer}>
									<Card url={resource.link} appearance="inline" truncateInline />
									<Box xcss={styles.buttonContainer}>
										<IconButton
											label="remove-audio-resource"
											appearance="subtle"
											icon={CrossIcon}
											spacing="compact"
											onClick={() => removeAdditionalResource(resource.ari)}
										/>
									</Box>
								</Inline>
							))}
						</Stack>
						<SourcesInput
							additionalResources={localAdditionalResources}
							addAdditionalResource={addAdditionalResource}
							activeContent={activeContent}
						/>
					</Stack>
					<RefetchAudioButton
						buttonText={i18n.refetchAudioButtonText}
						source="sourcesPanel"
						shouldRequeue
						supportingContent={localAdditionalResources}
						isDisabled={!sourcesHaveDiverged}
					/>
				</Stack>
			</Stack>
		</ConfluenceSmartCardProvider>
	);
};
